import { useState } from 'react';
import { approvalActionLabel, approvalLabel, describeAssignmentRule, type ScheduleAssignment } from '@cornerstone/shared';
import { Button, Segmented } from '../components/ui';
import type { WriteGate } from '../nav/useWriteGate';
import { canDecideAssignment, currentApprovalReason, isOwnPendingRequest, workflowJson, type WorkflowScope } from './mock/ContentApproval';
const minuteToHHMM = (value: number) => `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;

export function ScheduleReview({ assignments, scope, onChanged, onOpen, onCreate, canCreate }: {
  assignments: ScheduleAssignment[]; scope: WorkflowScope; onChanged: () => void;
  onOpen: (assignment: ScheduleAssignment) => void;
  /** 빈 상태에서 곧장 스케줄을 만들러 간다. 주면 빈 상태에 버튼이 붙는다(검수 C16). */
  onCreate?: (() => void) | undefined;
  /** 그 버튼의 쓰기 게이트 — 권한이 없으면 숨기지 않고 비활성 + 이유. */
  canCreate?: WriteGate | undefined;
}) {
  const [filter, setFilter] = useState('pending');
  const [selectedId, setSelectedId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requests = assignments.filter(a => a.approval);
  const list = requests.filter(a => filter === 'all' || a.approval?.status === filter)
    .sort((a, b) => (b.approval?.requestedAt ?? b.createdAt).localeCompare(a.approval?.requestedAt ?? a.createdAt));
  const selected = list.find(a => a.id === selectedId) ?? list[0];
  const deviceName = (id: string) => scope.devices.find(d => d.id === id)?.name ?? '기기';
  async function decide(status: 'approved' | 'rejected') {
    if (!selected || busy) return;
    if (status === 'rejected' && !note.trim()) { setError('반려 사유를 입력하세요.'); return; }
    setBusy(true); setError('');
    try {
      await workflowJson(`schedule-assignments/${encodeURIComponent(selected.id)}/approval`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, note: note.trim(), revision: selected.revision }),
      });
      setNote(''); onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  // 자기 요청은 승인 전에 여기서 바로 거둬들인다(SCDS-87). 지워지지 않고 '요청 취소됨'으로 남는다.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const ownPending = !!selected && isOwnPendingRequest(scope, selected);
  async function cancelRequest() {
    if (!selected || busy) return;
    setBusy(true); setError('');
    try {
      await workflowJson(`schedule-assignments/${encodeURIComponent(selected.id)}?scope=batch`, { method: 'DELETE' });
      setConfirmCancel(false); onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  return <section aria-label="스케줄 승인 요청" className="flex min-h-0 flex-1 flex-col overflow-auto">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <Segmented value={filter} onChange={value => { setFilter(value); setNote(''); }} options={[
        { value: 'pending', label: `승인 대기 ${requests.filter(a => a.approval?.status === 'pending').length}` },
        { value: 'approved', label: '승인됨' }, { value: 'rejected', label: '반려됨' }, { value: 'cancelled', label: '요청 취소됨' }, { value: 'all', label: '전체' },
      ]} />
      <Button onClick={onChanged} disabled={busy}>새로고침</Button>
    </div>
    {error && <p role="alert" className="mb-3 text-red">{error}</p>}
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-2">
        {/* 빈 상태는 사실만 말하고 끝내지 않는다 — 여기서 할 수 있는 다음 행동을 붙인다(검수 C16). */}
        {!list.length && <div role="status" className="flex flex-col items-start gap-3 rounded border border-line bg-panel-2 p-6">
          <p className="text-text-dim">{filter === 'all' ? '승인 요청이 아직 없습니다.'
            : '해당 상태의 요청이 없습니다.'}</p>
          <p className="text-[13px] text-text-dim">{requests.length
            ? '다른 상태를 골라 처리한 요청을 확인할 수 있습니다.'
            : '승인이 필요한 사람이 스케줄을 등록·수정·삭제하면 이곳에 승인 요청이 쌓입니다.'}</p>
          <div className="flex flex-wrap gap-2">
            {filter !== 'all' && !!requests.length && <Button onClick={() => { setFilter('all'); setNote(''); }}>전체 보기</Button>}
          </div>
        </div>}
        {list.map(a => <button key={a.id} disabled={busy} onClick={() => { setSelectedId(a.id); setNote(''); setError(''); setConfirmCancel(false); }}
          className={`w-full rounded border p-3 text-left ${selected?.id === a.id ? 'border-accent bg-accent-soft' : 'border-line'}`}>
          <div className="flex justify-between gap-3"><strong>{a.name}</strong><span>{approvalActionLabel(a.approval!.action)} {approvalLabel(a.approval!.status)}</span></div>
          <p className="mt-1 text-sm text-text-dim">{deviceName(a.deviceId)} · {a.layer === 'event' ? '이벤트' : '기본'} · {a.startDate}</p>
        </button>)}
      </div>
      {selected && <div className="space-y-3 rounded border border-line p-4">
        <h3 className="font-semibold">{selected.name} · {deviceName(selected.deviceId)}</h3>
        <p>{approvalActionLabel(selected.approval!.action)} {approvalLabel(selected.approval!.status)}</p>
        <p className="text-sm">{describeAssignmentRule(selected)}</p>
        <p className="text-sm">{selected.blocks.map(b => `${minuteToHHMM(b.startMinute)}–${minuteToHHMM(b.endMinute)}`).join(', ')}</p>
        <p className="text-sm">{currentApprovalReason(scope, selected)}</p>
        {selected.approval!.status === 'pending' && <p className="text-sm text-text-dim">{selected.approval!.replacesId
          ? (selected.approval!.action === 'delete' ? '승인하면 기존 스케줄이 삭제됩니다. 승인 전까지 기존 스케줄이 그대로 재생됩니다.'
            : '승인하면 이 내용이 기존 스케줄을 대체합니다. 승인 전까지 기존 스케줄이 그대로 재생됩니다.')
          : '승인 전에는 재생되지 않습니다.'}</p>}
        {selected.approval!.note && <p className="text-sm">검토 의견: {selected.approval!.note}</p>}
        {selected.approval!.decidedAt && <p className="text-sm text-text-dim">처리 시각: {new Date(selected.approval!.decidedAt).toLocaleString('ko-KR')}</p>}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onOpen(selected)}>스케줄 상세</Button>
          {ownPending && (confirmCancel
            ? <><Button variant="danger" disabled={busy} onClick={() => void cancelRequest()}>요청 취소 확정</Button>
              <Button disabled={busy} onClick={() => setConfirmCancel(false)}>그대로 두기</Button></>
            : <Button variant="danger" disabled={busy} title="승인 전에 요청을 거둬들입니다. '요청 취소됨'에 남습니다."
              onClick={() => setConfirmCancel(true)}>승인 요청 취소</Button>)}
        </div>
        {canDecideAssignment(scope, selected) && selected.approval!.status === 'pending' && <>
          <textarea aria-label="검토 의견 / 반려 사유" placeholder="검토 의견 / 반려 사유" value={note} disabled={busy}
            onChange={e => setNote(e.target.value)} className="block w-full rounded border border-line bg-panel-2 p-2" />
          <div className="flex gap-2"><Button variant="primary" disabled={busy} onClick={() => void decide('approved')}>승인</Button>
            <Button disabled={busy} onClick={() => void decide('rejected')}>반려</Button></div>
        </>}
      </div>}
    </div>
  </section>;
}
